import type { ReactElement } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import BugReportOutlinedIcon from "@mui/icons-material/BugReportOutlined";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  FEEDBACK_REPORT_KINDS,
  FEEDBACK_REPORT_MAX_MESSAGE_LENGTH,
  type FeedbackReportKind,
} from "@shared/types/feedback_reports.ts";
import { PageContainer } from "../../components/primitives/layout/PageContainer";
import { PageHeader } from "../../components/primitives/layout/PageHeader";
import { Section } from "../../components/primitives/layout/Section";
import { ROUTES } from "../../route_paths";
import { useAccountFeedback } from "./useAccountFeedback";

const KIND_ICONS: Record<FeedbackReportKind, ReactElement> = {
  bug: <BugReportOutlinedIcon />,
  idea: <LightbulbOutlinedIcon />,
  other: <MoreHorizIcon />,
};

/** The account's feedback section: report a bug, suggest an idea or say anything else. */
export function AccountFeedback() {
  const { t } = useTranslation();
  const form = useAccountFeedback();

  return (
    <PageContainer embedded>
      <PageHeader
        size="compact"
        title={t("account.feedback.title")}
        subtitle={t("account.feedback.subtitle")}
      />
      <Section>
        {form.sent ? (
          <Stack gap={2} alignItems="flex-start">
            <Alert severity="success">{t("account.feedback.thanks")}</Alert>
            <Button variant="outlined" onClick={form.sendAnother}>
              {t("account.feedback.sendAnother")}
            </Button>
          </Stack>
        ) : (
          <Stack
            component="form"
            gap={2}
            onSubmit={(e) => {
              e.preventDefault();
              if (form.canSend) void form.send();
            }}
          >
            {form.error && <Alert severity="error">{form.error}</Alert>}
            <ToggleButtonGroup
              exclusive
              size="small"
              value={form.kind}
              onChange={(_, kind) => kind && form.setKind(kind)}
              aria-label={t("account.feedback.kind")}
            >
              {FEEDBACK_REPORT_KINDS.map((kind) => (
                <ToggleButton key={kind} value={kind}>
                  {KIND_ICONS[kind]}
                  {t(`account.feedback.kinds.${kind}`)}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
            <TextField
              label={t("account.feedback.message")}
              value={form.message}
              onChange={(e) => form.setMessage(e.target.value)}
              multiline
              minRows={5}
              fullWidth
              required
              slotProps={{ htmlInput: { maxLength: FEEDBACK_REPORT_MAX_MESSAGE_LENGTH } }}
            />
            {form.verificationId && (
              <Stack direction="row">
                <Chip
                  component={Link}
                  to={ROUTES.verify.path({ userEventVerificationId: form.verificationId })}
                  clickable
                  size="medium"
                  label={t("account.feedback.aboutResult")}
                  onDelete={form.clearVerification}
                />
              </Stack>
            )}
            <Stack direction="row" justifyContent="flex-end">
              <Button type="submit" variant="contained" disabled={!form.canSend}>
                {form.sending ? t("account.feedback.sending") : t("account.feedback.send")}
              </Button>
            </Stack>
          </Stack>
        )}
      </Section>
    </PageContainer>
  );
}
